import { useCallback, useEffect, useState } from 'react';

import {
  getEmbedSession,
  getGlobalEmbedSession,
  subscribeEmbedSession,
  type EmbedSessionPayload
} from '../widget-lib/embed-session-store';

/** Проверяет JWT exp: true если токен истёк или истечёт через `bufferSec`. */
function isTokenExpired(token: string, bufferSec = 300): boolean {
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    const exp = typeof payload.exp === 'number' ? payload.exp : 0;
    return exp > 0 && (exp * 1000 < Date.now() + bufferSec * 1000);
  } catch {
    return true;
  }
}

/**
 * Реактивное чтение сессии embed из localStorage (тот же slug, что в query).
 *
 * Раньше использовался `useSyncExternalStore`; в связке с React 19 + несколькими useAsync
 * в `EmbedBootstrapProvider` это давало вложенные обновления (ошибка #185) после staff-login.
 * Подписка через `useEffect` + `setState` устраняет каскад.
 */
export function useEmbedSession(orgSlug: string | null): EmbedSessionPayload | null {
  const readSession = useCallback(
    () => getEmbedSession(orgSlug) ?? getGlobalEmbedSession(),
    [orgSlug]
  );
  const [session, setSession] = useState<EmbedSessionPayload | null>(() => readSession());

  useEffect(() => {
    setSession(readSession());
    const unsubscribe = subscribeEmbedSession(() => {
      setSession(readSession());
    });
    return unsubscribe;
  }, [readSession]);

  // Автообновление истекшего или скоро истекающего токена при загрузке
  useEffect(() => {
    const s = session;
    if (!s?.accessToken || !s.refreshToken) return;
    if (!isTokenExpired(s.accessToken)) return;

    let cancelled = false;
    import('../widget-lib/embed-auth-bridge').then((m) => {
      if (cancelled) return;
      m.tryRefreshEmbedAccessToken().then((newTok) => {
        if (!cancelled && newTok) {
          setSession(readSession());
        }
      });
    });
    return () => { cancelled = true; };
  }, [readSession, session]);

  return session;
}
